import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './auth/AuthContext';
import { DEFAULT_USER_SETTINGS, fetchUserSettings, updateUserSettings, type UserSettings } from './user-settings-api';

const STORAGE_KEY = 'lk-room-preferences';

/**
 * Room display preferences, local to the viewer: they never affect the published
 * track, other participants, or recordings.
 *
 * Guests persist to localStorage only. Authenticated users persist to the API as
 * well, so preferences follow them across devices; localStorage doubles as a cache
 * so the UI starts with the right value instead of flashing defaults.
 */
interface RoomPreferencesValue extends UserSettings {
    toggleMirrorLocalVideo: () => void;
    toggleHideParticipantsWithoutVideo: () => void;
}

const RoomPreferencesContext = createContext<RoomPreferencesValue | null>(null);

function loadCachedSettings(): UserSettings {
    if (typeof window === 'undefined') return DEFAULT_USER_SETTINGS;
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return DEFAULT_USER_SETTINGS;
        return { ...DEFAULT_USER_SETTINGS, ...JSON.parse(stored) };
    } catch {
        return DEFAULT_USER_SETTINGS;
    }
}

function saveCachedSettings(settings: UserSettings): void {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {}
}

export const RoomPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [settings, setSettings] = useState<UserSettings>(loadCachedSettings);
    const settingsRef = useRef(settings);
    settingsRef.current = settings;

    // Publishes the mirror preference to <html>, where component-styles.css keys its
    // opt-out off it. Belongs at provider level, not in a room component: the camera
    // preview renders before any room exists and must honour the preference too.
    // Only the opt-out is marked — an absent attribute means livekit's own mirroring.
    useEffect(() => {
        const root = document.documentElement;
        if (settings.mirror_local_video) {
            root.removeAttribute('data-um-mirror-local-video');
        } else {
            root.setAttribute('data-um-mirror-local-video', 'false');
        }
    }, [settings.mirror_local_video]);

    // Refine the optimistic localStorage value with the server's copy.
    useEffect(() => {
        if (!isAuthenticated) return;

        let cancelled = false;
        (async () => {
            try {
                const remote = await fetchUserSettings();
                if (cancelled) return;
                setSettings(remote);
                saveCachedSettings(remote);
            } catch (error) {
                // Keep the cached value: it is a better guess than the defaults.
                console.warn('RoomPreferences: failed to load settings from API', error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);

    const update = useCallback(
        (patch: Partial<UserSettings>) => {
            const next = { ...settingsRef.current, ...patch };
            setSettings(next);
            saveCachedSettings(next);

            if (!isAuthenticated) return;

            // A failed sync leaves the preference applied locally: a display toggle is
            // not worth undoing the user's action over a network error.
            updateUserSettings(patch).catch((error) => {
                console.warn('RoomPreferences: failed to persist settings to API', error);
            });
        },
        [isAuthenticated]
    );

    const toggleMirrorLocalVideo = useCallback(() => update({ mirror_local_video: !settingsRef.current.mirror_local_video }), [update]);

    const toggleHideParticipantsWithoutVideo = useCallback(
        () => update({ hide_participants_without_video: !settingsRef.current.hide_participants_without_video }),
        [update]
    );

    const value: RoomPreferencesValue = {
        ...settings,
        toggleMirrorLocalVideo,
        toggleHideParticipantsWithoutVideo
    };

    return <RoomPreferencesContext.Provider value={value}>{children}</RoomPreferencesContext.Provider>;
};

/**
 * Returns defaults outside a provider so tiles and the control bar can be rendered
 * standalone (embed usage, tests) without crashing.
 */
export function useRoomPreferences(): RoomPreferencesValue {
    const context = useContext(RoomPreferencesContext);
    if (!context) {
        return {
            ...DEFAULT_USER_SETTINGS,
            toggleMirrorLocalVideo: () => {},
            toggleHideParticipantsWithoutVideo: () => {}
        };
    }
    return context;
}
